<script setup lang="ts">
import { ref, Ref, onMounted, watch } from 'vue';
import i18n from '@/lang/i18n';
import { useRouter } from 'vue-router';
import Api from '@/services/api/ApiServiceFabrick';
import AuthorityCacheApi from '@/services/models/AuthorityCacheApi';
import SearchForm from '@/components/GpsTrackerSetting/SearchForm.vue';
import GpsTrackerSettingTable from '@/components/GpsTrackerSetting/GpsTrackerSettingTable.vue';
import PaginateWithCount from '@/components/PaginateWithCount.vue';
import getGpsTrackerSettingListRequest from '@/types/requests/getGpsTrackerSettingList'
import getGpsTrackerSettingListResponse from '@/types/responses/getGpsTrackerSettingList'
import getGpsTrackerSettingResponse from '@/types/responses/getGpsTrackerSetting'

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
}>()

const router = useRouter()
const isLoading: Ref<boolean> = ref(false)
const showableDeleteButton = ref(false)
const gpsTrackerSettings: Ref<getGpsTrackerSettingResponse[]> = ref([])
const currentPage = ref(1)
const totalPage = ref(1)
const searchParams: Ref<getGpsTrackerSettingListRequest> = ref({ operation_office_id: null, page: 1 })

function moveToForm() {
  router.push('/gps-tracker-settings/form/')
}

function fetchList() {
  isLoading.value = true
  Api().getGpsTrackerSettingList(searchParams.value)
    .then((data: getGpsTrackerSettingListResponse) => {
      gpsTrackerSettings.value = data.gps_tracker_settings
      totalPage.value = data.total_page
      currentPage.value = data.current_page
    })
    .finally(() => {
      isLoading.value = false
    })
}

function searchList(params: { operationOffice: { id: number } | null }) {
  searchParams.value = { operation_office_id: params.operationOffice?.id ?? null, page: 1 }
  currentPage.value = 1
  fetchList()
}

function onDeleteClick(id: number) {
  if (!confirm(i18n.global.t('common.confirmationModal.confirmDelete'))) return
  Api().deleteGpsTrackerSetting(id)
    .then(() => {
      fetchList()
    })
    .catch(() => {
      props.showFlash(i18n.global.t('pages.common.messages.failedDelete'), 'error', true)
    })
}

watch(currentPage, (page) => {
  searchParams.value = { ...searchParams.value, page }
  fetchList()
})

onMounted(() => {
  fetchList()
  AuthorityCacheApi.getWithParse().then((authorityList) => {
    showableDeleteButton.value = !!authorityList['octlink.gps_tracker_settings.delete']
  })
})

</script>

<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex justify-content-between mb-1">
        <h4 class="mt-0">
          {{ i18n.global.t('pages.gpsTrackerSetting.title') }}
        </h4>
        <div class="d-flex justify-end mb-5">
          <v-btn class="exec-btn mt-0" @click="moveToForm">
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
        </div>
      </div>
      <div>
        <GpsTrackerSettingTable
          class="mb-2"
          :isLoading="isLoading"
          :gpsTrackerSettings="gpsTrackerSettings"
          :showDeleteButton="showableDeleteButton"
          @delete="onDeleteClick"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :showItemCount="false"
        />
      </div>
    </div>
    <SearchForm
      :isLoading="isLoading"
      @search="searchList"
    />
  </div>
</template>
